/**
 * `/shipments/:id` — customer-facing shipment detail (Phase 2 Stream C).
 *
 * Migrated from PSM `portal/src/pages/ShipmentDetailPage.tsx` (~330 LOC).
 *
 * N+1 fix (audit PR #52):
 *   The PSM original fetched `/shipments?limit=200` and filtered the result
 *   on `id` in the browser, which scaled O(n) with the customer's
 *   shipment count and leaked rows the user wouldn't view. This
 *   implementation calls the server-side `/api/portal/shipments/:id`
 *   endpoint added in Phase 2 Stream C, which delegates to the PSM
 *   Internal API `GET /api/internal/portal/shipments/:id?customer_id=…`
 *   for a single-row authorization check.
 *
 * Out of scope here (kept lean for Phase 2):
 *   - Document attachments (Phase 3 Stream E)
 *   - SIG / shipment-group grouping (Phase 2 Stream C closeout)
 *   - Download All Docs CTA (depends on Stream E)
 *
 * Spec: docs/specs/PORTAL_INTERNAL_API_CONTRACT.md §2.4
 *       docs/specs/PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.2 (Phase 2 Stream C)
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PortalApiError, portalApi } from '../lib/apiClient';
import type { PortalShipmentDetail } from '@customer-portal/shared';
import { Card, CardBody, CardHeader, CardTitle, ErrorState, Spinner } from '../components/ui';

interface DetailEnvelope {
  ok: true;
  data: { shipment: PortalShipmentDetail };
}

type State =
  | { kind: 'loading' }
  | { kind: 'success'; shipment: PortalShipmentDetail }
  | { kind: 'not_found' }
  | { kind: 'error'; code: string };

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' });
}

function Field({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div>
      <p
        style={{
          margin: 0,
          fontSize: '0.6875rem',
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.025em',
          fontWeight: 600,
        }}
      >
        {label}
      </p>
      <p style={{ margin: '0.125rem 0 0', fontSize: '0.875rem', color: '#111827' }}>{value}</p>
    </div>
  );
}

function BackLink(): JSX.Element {
  return (
    <p style={{ margin: '0 0 1rem' }}>
      <Link to="/shipments" style={{ color: '#1f4ed8', textDecoration: 'none' }}>
        ← Back to shipment list
      </Link>
    </p>
  );
}

export function ShipmentDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!id) {
      setState({ kind: 'not_found' });
      return undefined;
    }
    const ctrl = new AbortController();
    setState({ kind: 'loading' });
    void (async () => {
      try {
        // N+1 fix: server-side :id lookup, NOT list+filter.
        const res = await portalApi<DetailEnvelope>(
          `/api/portal/shipments/${encodeURIComponent(id)}`,
          { signal: ctrl.signal },
        );
        setState({ kind: 'success', shipment: res.data.shipment });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalApiError) {
          if (err.status === 404) {
            setState({ kind: 'not_found' });
            return;
          }
          setState({ kind: 'error', code: err.code });
          return;
        }
        setState({ kind: 'error', code: 'UNKNOWN' });
      }
    })();
    return () => ctrl.abort();
  }, [id]);

  if (state.kind === 'loading') {
    return (
      <section data-testid="shipment-detail-page">
        <BackLink />
        <Spinner block label="Loading" />
      </section>
    );
  }
  if (state.kind === 'not_found') {
    return (
      <section data-testid="shipment-detail-page">
        <BackLink />
        <ErrorState
          title={id ? `Shipment #${id} was not found.` : 'No shipment ID was specified.'}
          description="You may not have permission, or this shipment may have been deleted."
        />
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section data-testid="shipment-detail-page">
        <BackLink />
        <ErrorState
          title="Failed to load shipment details"
          description={`(${state.code})`}
          onRetry={() => {
            // Trigger refetch by toggling a dummy state — simplest: navigate(0)
            // but we don't have navigate here. Just reload the page id-effect
            // by setting loading; the parent useEffect re-runs when state
            // changes are forced via a key, so users can also hit the browser
            // back/forward. For now, ask the user to retry by navigation.
            window.location.reload();
          }}
        />
      </section>
    );
  }

  const s = state.shipment;
  return (
    <section data-testid="shipment-detail-page">
      <BackLink />
      <header style={{ marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Shipment #{s.id}</h1>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
          <span style={{ fontFamily: 'monospace' }}>{s.chassis_number}</span>
        </p>
      </header>

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '2fr 1fr' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Card aria-label="Shipment details">
            <CardHeader>
              <CardTitle level={2}>Shipment details</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                <Field label="Vessel name" value={s.vessel_name ?? '—'} />
                <Field label="Voyage No." value={s.voyage_number ?? '—'} />
                <Field label="Shipping carrier" value={s.shipping_carrier ?? '—'} />
                <Field
                  label="B/L No."
                  value={<span style={{ fontFamily: 'monospace' }}>{s.bl_number ?? '—'}</span>}
                />
                <Field label="Transport status" value={s.transport_status || '—'} />
                <Field
                  label="Chassis number"
                  value={<span style={{ fontFamily: 'monospace' }}>{s.chassis_number}</span>}
                />
              </div>
            </CardBody>
          </Card>

          <Card aria-label="Route">
            <CardHeader>
              <CardTitle level={2}>Route</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                <Field label="Pickup location" value={s.pickup_location || '—'} />
                <Field label="Loading port" value={s.loading_port ?? '—'} />
                <Field label="Destination port" value={s.destination_port ?? '—'} />
                <Field label="Delivery location" value={s.delivery_location || '—'} />
              </div>
            </CardBody>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Card aria-label="Key dates">
            <CardHeader>
              <CardTitle level={2}>Key dates</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Field label="Transport date" value={formatDate(s.transport_date)} />
                <Field label="ETD (departure date)" value={formatDate(s.etd)} />
                <Field
                  label="ETA (arrival date)"
                  value={
                    <span style={{ color: '#1f4ed8' }}>
                      {formatDate(s.eta ?? s.estimated_delivery_date)}
                    </span>
                  }
                />
                <Field label="Delivery date" value={formatDate(s.delivery_date)} />
              </div>
            </CardBody>
          </Card>

          <Card aria-label="Related links">
            <CardHeader>
              <CardTitle level={2}>Related links</CardTitle>
            </CardHeader>
            <CardBody>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                <Link to={`/vehicles/${s.vehicle_id}`} style={{ color: '#1f4ed8' }}>
                  View vehicle details →
                </Link>
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    </section>
  );
}
